/**
 * REALTY-MEDIA-QA-A1 — shared image-file accept/classification contract.
 *
 * IMAGE_ACCEPT is for profile + Property photos/plans (JPEG/PNG/HEIC/HEIF).
 * CHAT_IMAGE_ACCEPT is chat-only and also preserves WebP/GIF picker support
 * matching MessageUploadView — do not widen Property/profile to WebP/GIF.
 */

export const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".heic", ".heif"];

export const IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/pjpeg",
  "image/png",
  "image/heic",
  "image/heif",
];

/** Recommended <input accept> — MIME + extensions for reliable pickers. */
export const IMAGE_ACCEPT =
  "image/jpeg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif";

/**
 * Chat message / chat-logo image pickers — preserves WebP/GIF plus HEIC/HEIF.
 * Do not use unrestricted image/*.
 */
export const CHAT_IMAGE_ACCEPT =
  "image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif";

export const IMAGE_ACCEPT_HINT = "JPEG, PNG, HEIC або HEIF.";

export const UNSUPPORTED_IMAGE_MESSAGE =
  "Підтримуються лише JPG, JPEG, PNG, HEIC або HEIF.";

/** Chat logo / message image picker — preserves WebP/GIF in the message. */
export const CHAT_UNSUPPORTED_IMAGE_MESSAGE =
  "Підтримуються лише JPG, JPEG, PNG, WebP, GIF, HEIC або HEIF.";

const EXT_SET = new Set(IMAGE_EXTENSIONS);
const MIME_SET = new Set(IMAGE_MIME_TYPES);
const HEIC_EXTS = new Set([".heic", ".heif"]);
const HEIC_MIMES = new Set(["image/heic", "image/heif"]);
const PREVIEWABLE_EXTS = new Set([".jpg", ".jpeg", ".png", ".webp", ".gif"]);
const PREVIEWABLE_MIMES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/pjpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);
const CHAT_EXTRA_EXTS = new Set([".webp", ".gif"]);
const CHAT_EXTRA_MIMES = new Set(["image/webp", "image/gif"]);

export function extensionOf(filename) {
  if (!filename || typeof filename !== "string") return "";
  const idx = filename.lastIndexOf(".");
  if (idx < 0) return "";
  return filename.slice(idx).toLowerCase();
}

export function isHeicLike(file) {
  if (!file) return false;
  const mime = (file.type || "").toLowerCase();
  if (HEIC_MIMES.has(mime)) return true;
  return HEIC_EXTS.has(extensionOf(file.name));
}

export function canLocalPreview(file) {
  if (!file) return false;
  if (isHeicLike(file)) return false;
  const mime = (file.type || "").toLowerCase();
  if (mime && PREVIEWABLE_MIMES.has(mime)) return true;
  if (!mime) return PREVIEWABLE_EXTS.has(extensionOf(file.name));
  return PREVIEWABLE_EXTS.has(extensionOf(file.name));
}

/**
 * Accept when MIME is known-allowed, OR MIME is empty/unknown but the
 * extension is supported. Never trust MIME or filename alone on the
 * server — this is a client UX gate only.
 */
export function isAcceptedImageFile(file) {
  if (!file) return false;
  const mime = (file.type || "").toLowerCase();
  const ext = extensionOf(file.name);

  if (mime && MIME_SET.has(mime)) return true;
  if ((!mime || mime === "application/octet-stream") && EXT_SET.has(ext)) {
    return true;
  }
  // Some browsers report empty type for HEIC with a valid extension.
  if (!mime && EXT_SET.has(ext)) return true;
  return false;
}

/**
 * Chat message image gate — JPEG/PNG/WebP/GIF/HEIC/HEIF.
 * Keeps Property/profile `isAcceptedImageFile` free of WebP/GIF.
 */
export function isAcceptedChatImageFile(file) {
  if (!file) return false;
  if (isAcceptedImageFile(file)) return true;
  const mime = (file.type || "").toLowerCase();
  const ext = extensionOf(file.name);
  if (mime && CHAT_EXTRA_MIMES.has(mime)) return true;
  if ((!mime || mime === "application/octet-stream") && CHAT_EXTRA_EXTS.has(ext)) {
    return true;
  }
  if (!mime && CHAT_EXTRA_EXTS.has(ext)) return true;
  return false;
}

export function classifyImageFile(file) {
  if (!file) {
    return {
      accepted: false,
      heicLike: false,
      canPreview: false,
      error: UNSUPPORTED_IMAGE_MESSAGE,
    };
  }
  const accepted = isAcceptedImageFile(file);
  const heicLike = isHeicLike(file);
  return {
    accepted,
    heicLike,
    canPreview: accepted && canLocalPreview(file),
    error: accepted ? null : UNSUPPORTED_IMAGE_MESSAGE,
  };
}
